import { h } from 'preact';
import { useEffect, useRef, useState } from 'preact/hooks';
import { usePlayerContext, useStyles } from '../../../../../common/hooks';
import { getVolumeIcon } from '../../../../../common/utils/tools';
import { Button, ButtonLabel, Slider } from '../../../common';
import { VOLUME_BUTTON_SIZE, VOLUME_HIDDEN_STYLES, VOLUME_SLIDER_DEFAULT_DIALOG_STYLE, VOLUME_SLIDER_SIZE, VOLUME_VISIBLE_STYLES } from './styles';

import { TVShortcutController } from '../../../../../../core/shortcut/tvshortcut';
import { BACK_BTN_SHORTCUTS } from '../../../../../../core/shortcut/types';
import { UI_IS_INTERACTING } from '../../../../../../store/types';
import { USER_CLICK, USER_MUTE, USER_VOLUME_CHANGE } from '../../../../../../types';
import { useDialogContext, useRegisterDialog } from '../../../../../common/context/DialogContext';
import { OVERLAY_COLOR_OPAQUE } from '../../../../theme/colors';
import { RESIZE_FACTOR_EXP_CTRL_RESPONSIVE, VOLUME_DIALOG_LAYER } from '../../../../theme/constants';

function Volume({
  volume,
  muted,
  medium,
  extraSmall,
  small,
  name,
  disableFocus,
  ...extraProps
}) {
  const resolveStateToSliderVal = (vol) => Math.ceil((100 * vol)).toFixed(2);

  const player = usePlayerContext();
  const dialogCtx = useDialogContext();
  const activeIconRef = useRef(null);
  const { store } = usePlayerContext();
  const [volumeState, setVolumeState] = useState(volume);
  const [sliderVal, setSliderVal] = useState(resolveStateToSliderVal(volumeState));
  const [sliding, setSliding] = useState(false);
  const [opened, setOpened] = useState(false);
  const setStyles = useStyles();

  useRegisterDialog({
    id: name, openingMode: 'over', state: [opened, setOpened]
  });

  useEffect(() => !sliding && setVolumeState(volume), [volume]);

  useEffect(() => {
    setSliderVal(resolveStateToSliderVal(volumeState));

    if (volumeState !== volume) player.volume(volumeState);
  }, [volumeState]);

  useEffect(() => (sliding && muted && player.mute(false)), [sliding]);

  const toggleMute = () => {
    player.userEvents$.next({ action: USER_MUTE, value: !muted, source: USER_CLICK });
    player.mute(!muted);
  };

  const resizeFactor = (medium || small ? RESIZE_FACTOR_EXP_CTRL_RESPONSIVE : 1);
  const miniResizeFactor = resizeFactor / (small ? 1.80 : 1);

  const size = setStyles(VOLUME_SLIDER_SIZE);

  const handleSlide = (pos, maxPos) => {
    const val = pos / maxPos;

    setVolumeState(parseFloat(val.toFixed(2)));
  };

  const handleStopSlide = (pos, maxPos) => {
    player.userEvents$.next({ action: USER_VOLUME_CHANGE, source: USER_CLICK, value: pos / maxPos });
    setSliding(false);
  };

  const [sliderDynamicStyles, setSliderDynamicStyles] = useState(VOLUME_HIDDEN_STYLES);
  const dialogContainerRef = useRef(null);

  useEffect(() => {
    store.dispatch({ type: UI_IS_INTERACTING, payload: { isInteracting: opened } });
    setSliderDynamicStyles(opened ? VOLUME_VISIBLE_STYLES : VOLUME_HIDDEN_STYLES);
  }, [opened]);

  const onFocusSlider = (e) => {
    if (dialogContainerRef.current && dialogContainerRef.current.contains(e.target)) {
      setOpened(true);
    }
  };

  const onBlurContainer = () => {
    setTimeout(() => {
      if (dialogContainerRef.current && !dialogContainerRef.current.contains(document.activeElement)) {
        setOpened(false);
      }
    }, 0);
  };

  return (
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
    <div
      ref={dialogContainerRef}
      style={{
        position: 'relative'
      }}
      onBlur={onBlurContainer}
      onMouseEnter={() => {
        setOpened(true);
      }}
      onMouseLeave={() => {
        setOpened(false);
      }}
      onKeyDown={(e) => {
        if (dialogCtx.dialogOpened && TVShortcutController.getPrioritizedKeyCode(e, [BACK_BTN_SHORTCUTS])) {
          e.preventDefault();
          dialogCtx.closeAll();
          activeIconRef?.current?.focus();
        }
      }}
    >
      <Button
        ref={activeIconRef}
        type="icon"
        label="Son (m)"
        forceLabel
        styles={{
          ...(opened && {
            subWrapperStyle: {
              background: OVERLAY_COLOR_OPAQUE, borderRadius: '0 0 30px 30px'
            }
          })
        }}
        onClick={toggleMute}
        icon={getVolumeIcon({ volume, muted })}
        openingMode="over"
        xs
        name={name}
        disableFocus={disableFocus}
        zIndex={VOLUME_DIALOG_LAYER}
        size={setStyles(VOLUME_BUTTON_SIZE).width}
        hideLabel={opened}
        {...extraProps}
      />
      <div
        className="volume-slider-container"
        style={{
          ...VOLUME_SLIDER_DEFAULT_DIALOG_STYLE,
          ...sliderDynamicStyles
        }}
      >
        <ButtonLabel
          label="Son (m)"
          alignX="center"
          animated={false}
          visible={opened}
          xs
          zone="bottom"
          style={{ position: 'relative', top: 'unset' }}
        />
        <Slider
          value={muted ? 0 : sliderVal}
          strokeSize={6}
          cursorSize={size.cursorSize}
          trackSize={size.trackSize}
          onFocus={onFocusSlider}
          onBlur={() => setOpened(false)}
          onSlide={handleSlide}
          onStartSlide={() => setSliding(true)}
          onStopSlide={handleStopSlide}
          styles={{
            container: {
              paddingTop: 20 * resizeFactor,
              marginBottom: (size.cursorSize / 2) * miniResizeFactor,
              zIndex: VOLUME_DIALOG_LAYER,
              width: '100%'
            },
            outerTrack: {
              width: '100%'
            }
          }}
          vertical
          name="slider-volume"
          accessibleName="volume"
          disableFocus={disableFocus}
          showSlider={!extraSmall}
        />
      </div>
    </div>
  );
}

export default Volume;
